import { useState, useMemo } from 'react';
import { X, AlertTriangle, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMarkOutliers } from '../../hooks/useForecasts';
import { OutlierReason } from '../../services/forecasts.service';

interface OutlierDetectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  forecastId: string;
  forecastData: any[];
}

interface DayData {
  date: string;
  dayOfWeek: string;
  totalVolume: number;
  dataPointCount: number;
}

export default function OutlierDetectionModal({
  isOpen,
  onClose,
  forecastId,
  forecastData,
}: OutlierDetectionModalProps) {
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState<string>('HOLIDAY');
  const [notes, setNotes] = useState<string>('');

  const { mutate: markOutliers, isPending: isMarking } = useMarkOutliers();

  // Group forecast data by day
  const daysList = useMemo((): DayData[] => {
    if (!forecastData || forecastData.length === 0) return [];

    const dailyStats = new Map<string, { totalVolume: number; count: number }>();

    forecastData.forEach((dp) => {
      const date = new Date(dp.timestamp).toISOString().split('T')[0];
      if (!dailyStats.has(date)) {
        dailyStats.set(date, { totalVolume: 0, count: 0 });
      }
      const stats = dailyStats.get(date)!;
      stats.totalVolume += dp.predictedValue || 0;
      stats.count += 1;
    });

    const days: DayData[] = [];
    dailyStats.forEach((stats, dateStr) => {
      const date = new Date(dateStr);
      days.push({
        date: dateStr,
        dayOfWeek: date.toLocaleDateString('en-US', { weekday: 'long' }),
        totalVolume: Math.round(stats.totalVolume),
        dataPointCount: stats.count,
      });
    });

    // Sort by date (most recent first)
    return days.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [forecastData]);

  const handleToggleDate = (date: string) => {
    const newSelected = new Set(selectedDates);
    if (newSelected.has(date)) {
      newSelected.delete(date);
    } else {
      newSelected.add(date);
    }
    setSelectedDates(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedDates.size === daysList.length) {
      setSelectedDates(new Set());
    } else {
      setSelectedDates(new Set(daysList.map(d => d.date)));
    }
  };

  const handleMarkAsOutliers = () => {
    if (selectedDates.size === 0) {
      alert('Please select at least one day to mark as an outlier');
      return;
    }

    const dates = Array.from(selectedDates).sort();
    const startDate = dates[0];
    const endDate = dates[dates.length - 1];

    // Map reason string to enum
    const reasonEnum = reason === 'HOLIDAY' ? OutlierReason.HOLIDAY :
                       reason === 'SYSTEM_OUTAGE' ? OutlierReason.SYSTEM_OUTAGE :
                       reason === 'DATA_ERROR' ? OutlierReason.DATA_ERROR :
                       OutlierReason.OTHER;

    markOutliers({
      id: forecastId,
      dto: {
        startDate,
        endDate,
        reason: reasonEnum,
        notes: notes || `Marked ${selectedDates.size} day(s) as outlier`,
        excludeFromModel: true,
      },
    }, {
      onSuccess: () => {
        alert(`Successfully marked ${selectedDates.size} day(s) as outliers. These days will be excluded from future forecasts.`);
        setSelectedDates(new Set());
        setNotes('');
        onClose();
      },
      onError: (error: any) => {
        alert(`Failed to mark outliers: ${error?.response?.data?.message || error?.message}`);
      },
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-600 to-red-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Mark Outlier Days</h2>
                    <p className="text-orange-100 text-sm">
                      Select days to exclude from forecasting (holidays, system outages, etc.)
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-220px)]">
              {/* Reason and Notes */}
              <div className="mb-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Exclusion
                  </label>
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="HOLIDAY">Holiday</option>
                    <option value="SYSTEM_OUTAGE">System Outage</option>
                    <option value="DATA_ERROR">Data Error</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g., Christmas Day, Server maintenance"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-sm text-blue-700 mb-1">Selected Days</div>
                  <div className="text-2xl font-bold text-blue-900">{selectedDates.size}</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="text-sm text-gray-700 mb-1">Total Days</div>
                  <div className="text-2xl font-bold text-gray-900">{daysList.length}</div>
                </div>
              </div>

              {/* Days List */}
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">Select Days to Exclude</h3>
                  <button
                    onClick={handleSelectAll}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {selectedDates.size === daysList.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                <div className="max-h-96 overflow-y-auto space-y-2">
                  {daysList.map((day) => (
                    <div
                      key={day.date}
                      onClick={() => handleToggleDate(day.date)}
                      className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                        selectedDates.has(day.date)
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={selectedDates.has(day.date)}
                            onChange={() => handleToggleDate(day.date)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-5 w-5 text-orange-600 rounded focus:ring-orange-500"
                          />
                          <div>
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-gray-500" />
                              <span className="font-semibold text-gray-900">
                                {new Date(day.date).toLocaleDateString('en-US', {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })}
                              </span>
                              <span className="text-sm text-gray-500">({day.dayOfWeek})</span>
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              Total Volume: {day.totalVolume.toLocaleString()} | {day.dataPointCount} intervals
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkAsOutliers}
                disabled={selectedDates.size === 0 || isMarking}
                className="px-6 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isMarking ? 'Marking...' : `Exclude ${selectedDates.size} Day${selectedDates.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
